import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import ReactMarkdown from 'react-markdown';
import ReactQuill from 'react-quill';
import { Button } from 'reactstrap';
import { MessagingSendMessageMutation } from '../Mutations';
import { GET_MESSAGE_TEMPLATES, GET_CONVERSATION } from '../Queries';
import { CurrentUserContext } from '../../Context/CurrentUserContext';
import FileUpload from './components/FileUpload';
import ScheduleModal from './components/ScheduleModal';
import ShortcutHandler from './components/ShortcutHandler';
import CannedResponsePicker from './components/CannedResponsePicker';
import './MessageComposer.css';
import 'react-quill/dist/quill.snow.css';
import { PreferredDateTimeContext } from '../../Context/PreferredDateTimeContext';
import ConversationNoteComposer from './ConversationNoteComposer';

export default function MessageComposer({ conversationId, suggestedResponse, channelType: channelTypeProp, onSend: onSendProp, hideNoteComposer = false }) {
    const { currentUser } = useContext(CurrentUserContext);
    const [message, setMessage] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [attachmentResetKey, setAttachmentResetKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeSuggestion, setActiveSuggestion] = useState('');
    const [scheduledTime, setScheduledTime] = useState(null);
    const [isScheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [isTemplatePickerOpen, setTemplatePickerOpen] = useState(false);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [showShortcutHandler, setShowShortcutHandler] = useState(false);
    const [messageHtml, setMessageHtml] = useState('');
    const {preferredDateTimeInfo} = useContext(PreferredDateTimeContext);
    const textareaRef = useRef(null);
    const composerRef = useRef(null);
    
    const [messagingSendMessageMutation] = useMutation(MessagingSendMessageMutation);
    
    // Get conversation to fetch inbox members for mentions
    const { data: conversationData } = useQuery(GET_CONVERSATION, {
        variables: { id: conversationId },
        skip: !conversationId,
        fetchPolicy: 'cache-first'
    });
    const conversation = conversationData?.conversation;
    const driverId = conversation?.driver?.id;
    const isEmailChannel = channelTypeProp !== undefined
        ? channelTypeProp === 'EMAIL'
        : conversation?.channel?.channelType === 'EMAIL';
    
    const { data: templateData } = useQuery(GET_MESSAGE_TEMPLATES, {
        variables: { first: 100 },
        fetchPolicy: 'cache-first'
    });
    
    const templates = useMemo(() => {
        return templateData?.allMessageTemplates?.edges?.map(edge => edge.node) || [];
    }, [templateData]);

    useEffect(() => {
        setActiveSuggestion(suggestedResponse || '');
    }, [suggestedResponse]);

    const quillModules = useMemo(() => ({
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean'],
        ],
    }), []);

    const handleTextareaChange = (e) => {
        const newValue = e.target.value;
        const newCursorPos = e.target.selectionStart;
        setMessage(newValue);
        setCursorPosition(newCursorPos);
        const textBeforeCursor = newValue.substring(0, newCursorPos);

        // Check for / command
        const lastSlashIndex = textBeforeCursor.lastIndexOf('/');
        if (lastSlashIndex !== -1) {
            const textBeforeSlash = textBeforeCursor.substring(0, lastSlashIndex);
            const isAtStart = lastSlashIndex === 0;
            const isAfterWhitespace = /\s$/.test(textBeforeSlash);
            if (isAtStart || isAfterWhitespace) {
                const query = textBeforeCursor.substring(lastSlashIndex + 1);
                if (!/\s/.test(query)) {
                    setShowShortcutHandler(true);
                    return;
                }
            }
        }
        setShowShortcutHandler(false);
    };

    const handleKeyDown = (e) => {
        // Enter to send (unless Shift is held)
        if (e.keyCode === 13 && !e.shiftKey) {
            // Don't send if autocomplete is open
            if (showShortcutHandler) {
                return;
            }
            e.preventDefault();
            handleSubmit(e);
        }
        
        // Escape to close modals/autocompletes
        if (e.key === 'Escape') {
            setShowShortcutHandler(false);
            setTemplatePickerOpen(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const plainBody = isEmailChannel ? message.trim() : message;
        const htmlBody = isEmailChannel ? (messageHtml || plainTextToHtml(message)).trim() : '';
        if (loading || (!plainBody && selectedFiles.length === 0)) return;
        
        // If scheduled, just store it client-side for now
        let scheduledForTimestamp = null;
        if (scheduledTime) {
            scheduledForTimestamp = scheduledTime.unix();
        }
        setLoading(true);
        setError(null);

        try {
            const filesToUpload = await Promise.all(
                selectedFiles.map(
                    (file) =>
                        new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => {
                                const base64Data = reader.result.split(',')[1];
                                resolve({
                                    name: file.name,
                                    type: file.type,
                                    data: base64Data,
                                });
                            };
                            reader.onerror = reject;
                            reader.readAsDataURL(file);
                        })
                )
            );

            if (onSendProp) {
                await onSendProp(plainBody, isEmailChannel ? htmlBody : '', filesToUpload);
            } else {
                let variables = {
                    conversationId,
                    body: plainBody || (selectedFiles.length > 0 ? '📎 Attachment' : ''),
                    files: filesToUpload,
                };
                if (isEmailChannel && htmlBody && plainBody) {
                    variables.bodyHtml = htmlBody;
                }
                if (scheduledForTimestamp) {
                    variables.scheduledForTimestamp = scheduledForTimestamp;
                }
                const response = await messagingSendMessageMutation({ variables });

                const result = response?.data?.messagingSendMessage;
                if (!result?.ok) {
                    console.error('MessageComposer error: ', result?.errors);
                    setError(result?.errors?.toString() || 'Unknown error');
                    setLoading(false);
                    return;
                }
            }

            setLoading(false);
            setMessage('');
            setMessageHtml('');
            setSelectedFiles([]);
            setAttachmentResetKey(prev => prev + 1);
            setTemplatePickerOpen(false);
            setScheduledTime(null);
            setShowShortcutHandler(false);
        } catch (err) {
            console.error('MessageComposer error:', err);
            setLoading(false);
            setError('Error sending message');
        }
    };

    const handleAcceptSuggestion = () => {
        if (!activeSuggestion) return;
        if (isEmailChannel) {
            const suggestionHtml = activeSuggestion
                .split('\n')
                .map(line => `<p>${line || '<br />'}</p>`)
                .join('');
            setMessageHtml(suggestionHtml);
            setMessage(htmlToPlainText(suggestionHtml));
        } else {
            setMessage(activeSuggestion);
        }
        setActiveSuggestion('');
    };

    const handleRejectSuggestion = () => {
        setActiveSuggestion('');
    };

    const handleFilesSelected = (files) => {
        setSelectedFiles(files);
    };

    const plainTextToHtml = (textValue = '') => {
        if (!textValue) return '';
        return textValue
            .split('\n')
            .map(line => `<p>${line || '<br />'}</p>`)
            .join('');
    };

    const handleEmailEditorChange = (nextHtml) => {
        setMessageHtml(nextHtml || '');
        setMessage(htmlToPlainText(nextHtml || ''));
        if (showShortcutHandler) {
            setShowShortcutHandler(false);
        }
    };

    const htmlToPlainText = (htmlValue = '') => {
        if (!htmlValue) return '';
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlValue, 'text/html');
        const blockTags = new Set(['DIV', 'P', 'LI', 'TR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'TABLE']);
        let text = '';

        const walk = (node) => {
            if (!node) return;
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.nodeValue || '';
                return;
            }
            if (node.nodeType !== Node.ELEMENT_NODE) return;

            const tagName = node.tagName;
            if (tagName === 'BR') {
                text += '\n';
                return;
            }

            const isBlock = blockTags.has(tagName);
            if (isBlock && text && !text.endsWith('\n')) {
                text += '\n';
            }

            Array.from(node.childNodes).forEach(walk);

            if (isBlock && !text.endsWith('\n')) {
                text += '\n';
            }
        };

        Array.from(doc.body?.childNodes || []).forEach(walk);
        return text
            .replace(/\u00a0/g, ' ')
            .replace(/[ \t]+\n/g, '\n')
            .replace(/\n[ \t]+/g, '\n')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
    };

    const handleTemplateSelect = (template) => {
        if (isEmailChannel) {
            const htmlDraft = template?.contentHtml || plainTextToHtml(template?.content || '');
            const plainDraft = htmlToPlainText(htmlDraft) || template?.content || '';
            setMessageHtml(htmlDraft);
            setMessage(plainDraft);
            setTemplatePickerOpen(false);
            return;
        }

        const templatePlainContent = template?.content || htmlToPlainText(template?.contentHtml || '');
        if (!templatePlainContent) return;
        const currentPos = textareaRef.current?.selectionStart || message.length;
        const beforeCursor = message.substring(0, currentPos);
        const afterCursor = message.substring(currentPos);
        const separator = beforeCursor && !beforeCursor.endsWith('\n') ? '\n\n' : '';
        const newMessage = `${beforeCursor}${separator}${templatePlainContent}${afterCursor}`;
        setMessage(newMessage);
        setTemplatePickerOpen(false);
        
        setTimeout(() => {
            if (textareaRef.current) {
                const newPos = beforeCursor.length + separator.length + templatePlainContent.length;
                textareaRef.current.setSelectionRange(newPos, newPos);
                textareaRef.current.focus();
            }
        }, 0);
    };

    const handleCommandSelect = (command) => {
        if (command === 'schedule') {
            setScheduleModalOpen(true);
            setShowShortcutHandler(false);
        } else if (command === 'attach') {
            // Trigger file upload click
            const fileInput = document.querySelector('.file-upload-container input[type="file"]');
            if (fileInput) {
                fileInput.click();
            }
            setShowShortcutHandler(false);
        } else if (command === 'template') {
            setTemplatePickerOpen(true);
            setShowShortcutHandler(false);
        }
    };

    const formatScheduledTime = (time) => {
        if (!time) return '';
        return new Date(time).toLocaleString();
    };

    const editorHtmlValue = isEmailChannel ? (messageHtml || plainTextToHtml(message)) : '';
    const sendDisabled = loading || (!message.trim() && selectedFiles.length === 0);

    if (!currentUser) return <div></div>;

    return (
        <div className="CommsSessionInput" ref={composerRef}>
            {error && <div className="alert alert-danger mt-2">{error}</div>}
            <form onSubmit={handleSubmit} className="comms-send-form">
                {activeSuggestion && (
                    <div className="CommsSuggestedResponse">
                        <div className="CommsSuggestedResponseHeader">
                            <span className="CommsSuggestedResponseLabel">Suggested response</span>
                        </div>
                        <div className="CommsSuggestedResponseBody">
                            <ReactMarkdown>{activeSuggestion}</ReactMarkdown>
                        </div>
                        <div className="CommsSuggestedResponseActions">
                            <button
                                type="button"
                                className="CommsSuggestedResponseButton reject"
                                onClick={handleRejectSuggestion}
                                disabled={loading}
                            >
                                Reject
                            </button>
                            <button
                                type="button"
                                className="CommsSuggestedResponseButton accept"
                                onClick={handleAcceptSuggestion}
                                disabled={loading}
                            >
                                Use suggestion
                            </button>
                        </div>
                    </div>
                )}
                
                {scheduledTime && (
                    <div style={{ 
                        padding: '8px 12px', 
                        background: '#fef3c7', 
                        border: '1px solid #fbbf24', 
                        borderRadius: '6px', 
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <span style={{ fontSize: '13px', color: '#92400e' }}>
                            <i className="fa fa-clock-o" style={{ marginRight: '6px' }}></i>
                            Scheduled for {formatScheduledTime(scheduledTime)}
                        </span>
                        <Button
                            color="link"
                            size="sm"
                            onClick={() => setScheduledTime(null)}
                            style={{ padding: '4px', fontSize: '12px', backgroundColor: '#92400e' }}
                        >
                            Clear
                        </Button>
                    </div>
                )}
                
                <div style={{ position: 'relative' }}>
                    {isEmailChannel ? (
                        <div className="comms-send-rich-editor">
                            <ReactQuill
                                theme="snow"
                                value={editorHtmlValue}
                                onChange={handleEmailEditorChange}
                                modules={quillModules}
                                placeholder="Type an email..."
                                readOnly={loading}
                            />
                        </div>
                    ) : (
                        <textarea
                            ref={textareaRef}
                            className="comms-send-textarea"
                            rows="2"
                            name="body"
                            onKeyDown={handleKeyDown}
                            onChange={handleTextareaChange}
                            onClick={(e) => setCursorPosition(e.target.selectionStart)}
                            onSelect={(e) => setCursorPosition(e.target.selectionStart)}
                            onKeyUp={(e) => setCursorPosition(e.target.selectionStart)}
                            placeholder="Type a message... Use / for commands"
                            value={message}
                            disabled={loading}
                        />
                    )}

                    {!isEmailChannel && showShortcutHandler && (
                        <ShortcutHandler
                            textareaRef={textareaRef}
                            message={message}
                            setMessage={setMessage}
                            cursorPosition={cursorPosition}
                            templates={templates}
                            onTemplateSelect={handleTemplateSelect}
                            onCommandSelect={handleCommandSelect}
                            onClose={() => setShowShortcutHandler(false)}
                        />
                    )}
                </div>
                
                <div className="comms-send-toolbar">
                    <div style={{ position: 'relative' }}>
                        <FileUpload 
                            onFilesSelected={handleFilesSelected} 
                            disabled={loading} 
                            resetKey={attachmentResetKey} 
                        />
                    </div>
                    
                    <div style={{ position: 'relative', marginLeft: '8px' }}>
                        <Button
                            type="button"
                            color="light"
                            size="sm"
                            onClick={() => setTemplatePickerOpen(!isTemplatePickerOpen)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            disabled={loading}
                        >
                            <i className="fa fa-bookmark" aria-hidden="true"></i>
                            <span>Templates</span>
                        </Button>
                        
                        {isTemplatePickerOpen && (
                            <CannedResponsePicker
                                templates={templates}
                                onSelect={handleTemplateSelect}
                                onClose={() => setTemplatePickerOpen(false)}
                                isOpen={isTemplatePickerOpen}
                            />
                        )}
                    </div>
                    
                    <Button
                        type="button"
                        color="light"
                        size="sm"
                        onClick={() => setScheduleModalOpen(true)}
                        disabled={sendDisabled}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                    >
                        <i className="fa fa-clock-o" aria-hidden="true"></i>
                        <span>Schedule</span>
                    </Button>
                    <div className="comms-send-toolbar-spacer" />
                    <Button 
                        type="submit" 
                        color="primary"
                        size="sm"
                        disabled={sendDisabled}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                    >
                        {loading ? (
                            scheduledTime ? 'Scheduling message...' : 'Sending...'
                        ) : scheduledTime ? (
                            <>
                                <i className="fa fa-clock-o" aria-hidden="true"></i>
                                <span>Confirm schedule</span>
                            </>
                        ) : (
                            <>
                                <i className="fa fa-paper-plane" aria-hidden="true"></i>
                                <span>Send</span>
                            </>
                        )}
                    </Button>
                </div>
            </form>
            {isScheduleModalOpen &&
                <ScheduleModal
                    isOpen={isScheduleModalOpen}
                    toggle={() => setScheduleModalOpen(!isScheduleModalOpen)}
                    onSchedule={(time) => {
                        setScheduledTime(time);
                        setScheduleModalOpen(false);
                    }}
                    driverId={driverId}
                    userPrefferedTz={preferredDateTimeInfo?.timezone}
                />
            }
            {!hideNoteComposer && <ConversationNoteComposer conversationId={conversationId}/>}
        </div>
    );
}
